import React, { useState } from 'react';
import { z } from 'zod';
import { styled } from 'styled-components';
import type { BlockDefinition } from '../types';

// Home tab section — clean rewrite of legacy molecules/HomeTabSection (Strapi
// sections.new-home-tab-section + components.tab-data + components.tab-cards).
// Tabbed showcase: pick a tab; the panel shows that tab's image plus its cards
// (icon + description). Active tab via client useState (SSR renders tab 0).
// No styled-system / next/image / framer-motion.
export const homeTabSectionSchema = z.object({
  eyebrow: z.string().optional(),
  heading: z.string().min(1),
  description: z.string().optional(),
  tabs: z
    .array(
      z.object({
        title: z.string().min(1),
        imageUrl: z.string().optional(),
        cards: z
          .array(
            z.object({
              iconUrl: z.string().optional(),
              description: z.string().min(1),
            }),
          )
          .default([]),
      }),
    )
    .min(1)
    .max(8),
});

type HomeTabSectionProps = z.infer<typeof homeTabSectionSchema>;

const Section = styled.section`
  max-width: 144rem;
  margin: 0 auto;
  padding: 6rem 1.6rem;

  @media (min-width: 64em) {
    padding: 10rem 16rem;
  }
`;

const Header = styled.div`
  max-width: 72rem;
  margin: 0 auto 3rem;
  text-align: center;
`;

const Eyebrow = styled.p`
  margin: 0;
  color: #063091;
  font-family: 'DM Sans', sans-serif;
  font-size: 1.5rem;
  font-weight: 500;
  letter-spacing: 3px;
  text-transform: uppercase;
`;

const Heading = styled.h2`
  margin: 0.8rem 0 0;
  color: #0b1020;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 600;
  font-size: clamp(2.8rem, 4vw, 4.8rem);
`;

const Description = styled.p`
  margin: 0.8rem 0 0;
  color: #4e4f52;
  font-family: 'DM Sans', sans-serif;
  font-size: 1.6rem;
`;

const TabBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.8rem 2.4rem;
  margin: 3rem 0 0;
  border-bottom: 1px solid #e8efff;

  @media (min-width: 64em) {
    justify-content: space-between;
    gap: 0;
  }
`;

const Tab = styled.button<{ $active: boolean }>`
  appearance: none;
  border: none;
  background: none;
  padding: 1.2rem 0.4rem 1.6rem;
  margin-bottom: -1px;
  cursor: pointer;
  font-family: 'DM Sans', sans-serif;
  font-weight: 600;
  font-size: clamp(1.6rem, 2vw, 1.9rem);
  color: ${({ $active }) => ($active ? '#063091' : '#9aa1ad')};
  border-bottom: 2px solid ${({ $active }) => ($active ? '#063091' : 'transparent')};
  transition: color 0.3s ease, border-color 0.3s ease;
`;

// Full-width image banner with a row of cards overlapping its lower edge.
const Panel = styled.div`
  margin-top: 3.2rem;
`;

const Banner = styled.div`
  position: relative;
`;

const PanelImage = styled.img`
  width: 100%;
  height: clamp(28rem, 42vw, 54rem);
  border-radius: 2.4rem;
  object-fit: cover;
  display: block;
`;

const CardRow = styled.div`
  display: grid;
  gap: 1.6rem;
  margin-top: 2rem;
  padding: 0 1.6rem;

  @media (min-width: 64em) {
    grid-auto-flow: column;
    grid-auto-columns: 1fr;
    gap: 2.4rem;
    /* overlap the bottom of the banner */
    margin: -9rem 3.2rem 0;
    position: relative;
    z-index: 1;
  }
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  padding: 2.8rem;
  border-radius: 2rem;
  background: #ffffff;
  box-shadow: 0 3rem 6rem -2.4rem rgba(6, 48, 145, 0.28);
`;

const IconTile = styled.span`
  display: grid;
  place-items: center;
  width: 5.2rem;
  height: 5.2rem;
  border-radius: 1.4rem;
  background: #e8eeff;
  flex: 0 0 auto;
`;

const CardIcon = styled.img`
  width: 2.6rem;
  height: 2.6rem;
  object-fit: contain;
`;

const CardDesc = styled.p`
  margin: 0;
  color: #0b1020;
  font-family: 'DM Sans', sans-serif;
  font-weight: 500;
  font-size: clamp(1.6rem, 1.7vw, 1.9rem);
  line-height: 1.45;
`;

function HomeTabSection({ eyebrow, heading, description, tabs }: HomeTabSectionProps) {
  const [active, setActive] = useState(0);
  const tab = tabs[active] ?? tabs[0]!;

  return (
    <Section>
      <Header>
        {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
        <Heading>{heading}</Heading>
        {description ? <Description>{description}</Description> : null}
      </Header>

      <TabBar>
        {tabs.map((t, i) => (
          <Tab
            key={t.title}
            $active={i === active}
            onClick={() => setActive(i)}
            aria-pressed={i === active}
          >
            {t.title}
          </Tab>
        ))}
      </TabBar>

      <Panel>
        <Banner>
          {tab.imageUrl ? <PanelImage src={tab.imageUrl} alt={tab.title} /> : null}
          <CardRow>
            {tab.cards.map((card, i) => (
              <Card key={`${card.description}-${i}`}>
                {card.iconUrl ? (
                  <IconTile>
                    <CardIcon src={card.iconUrl} alt="" />
                  </IconTile>
                ) : null}
                <CardDesc>{card.description}</CardDesc>
              </Card>
            ))}
          </CardRow>
        </Banner>
      </Panel>
    </Section>
  );
}

export const homeTabSectionBlock: BlockDefinition<HomeTabSectionProps> = {
  type: 'homeTabSection',
  schema: homeTabSectionSchema,
  Renderer: HomeTabSection,
};
